import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Geometry } from 'geojson';
import {
  Table,
  Button,
  ButtonType
} from 'syngenta-digital-cropwise-react-ui-kit';
import { Maths } from 'helpers/maths';
import { useStore } from 'components/Store';
import useTablePaginationAndSort from 'hooks/useTablePaginationAndSort';
import tableSort, { getHeaderSortClassName } from 'helpers/tableSort';
import usePopulateMapData from 'hooks/usePopulateMapData';
import { FETCH_TYPES } from 'helpers/constants';
import { getPlanProperties } from 'screens/Plans/helpers/dataHelpers';
import cleanGeometry from 'helpers/cleanGeometry';
import FieldIcon from 'components/Icons/FieldIcon';
import CropIconMapping from 'components/CropIconMapping';
import LoadingSpinner from 'components/LoadingSpinner';
import MapIcon from 'components/Icons/MapIcon';
import EmptyTableState from 'components/EmptyTableState';
import NoPropertiesSelectedIcon from 'assets/properties-map-icon.svg';
import useTableScrollConfig from 'screens/Applications&WorkOrders/Components/DetailsTabs/hooks/useTableScrollConfig';
import { OPERATION_DETAILS_TABS_WRAPPER_SELECTOR } from 'screens/Applications&WorkOrders/Components/DetailsTabs/constants';
import ViewOnMapDrawer from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/AppliedAreaTab/components/ViewOnMapDrawer';
import { OperationPropertyVO } from 'screens/Applications&WorkOrders/Components/hooks/useOperationProperties';

const NOT_AVAILABLE = '-';
const PAGE_SIZE = 20;
const PROPERTIES_TABLE_SCROLL_X = 500;
// No tank-info section on this tab, matching the leading height/offset
// PlanProductsTab/InvoiceProductsTab already use for their own no-tank-info case.
const PROPERTIES_TABLE_OFFSET_EM = 39.75;
const PROPERTIES_TABLE_LEADING_HEIGHT = 8;
// This tab's container is `py-8` (32px bottom padding) — the hook's own
// default (16px) under-reserves it, letting the table grow into the extra
// 16px once there are enough rows to scroll. Same fix as InvoiceProductsTab's
// `INVOICE_PANEL_BOTTOM_PADDING_PX`.
const PROPERTIES_TAB_BOTTOM_PADDING_PX = 32;

interface PlanPropertiesRow {
  cropZoneId: string;
  cropZoneName?: string;
  fieldId?: string;
  fieldName?: string;
  fieldGeometry?: unknown;
  farmId?: string;
  farmName?: string;
  cropId?: string;
  cropName?: string;
  cropZoneGeometry?: Geometry;
  appliedArea?: number;
  appliedAreaPercent?: number;
}

export interface PlanPropertiesTabProps {
  cropZones: Array<{
    cropZoneIdValue: string;
    areaValue?: number;
    areaUnit?: string;
    coveragePct?: number;
  }>;
  /** Passed straight through to `ViewOnMapDrawer`'s Crop Season display (FR-051/FR-054). */
  cropSeasonNames?: string;
}

/**
 * Properties tab body — adds real column sorting (previously dead
 * `handleSort` stubs) and resolves all rows via a single tab-level
 * `getPlanProperties` fetch instead of one `usePopulateMapData` call per row.
 * Also renders a "View on Map" button (FR-051/FR-054), reusing
 * `ViewOnMapDrawer` verbatim — see `mapDrawerProperties` below for the
 * `OperationPropertyVO` mapping rationale.
 */
const PlanPropertiesTab: React.FC<PlanPropertiesTabProps> = ({
  cropZones,
  cropSeasonNames = ''
}) => {
  const [isMapDrawerOpen, setIsMapDrawerOpen] = useState(false);
  const {
    state: { cropSeasons }
  } = useStore();
  const {
    dataSorted: propertyLandingPageData,
    fieldsAndAreasGeoJSONCollection,
    loading,
    reloadData
  } = usePopulateMapData({
    fetchType: FETCH_TYPES.propertiesMap,
    cropSeasonsId: undefined
  }) as any;

  const hasFetchedRef = useRef(false);
  useEffect(() => {
    // A Plan's own crop zones can belong to any crop season, not just
    // whichever one(s) happen to be globally selected in the Breadcrumb —
    // `reloadData()` with no arguments falls back to that global selection
    // (`usePopulateMapData`'s `initialSettings` reads `selectedCropSeasons`
    // straight off localStorage), so a crop zone from any other season
    // would never appear in `propertyLandingPageData.properties` at all,
    // leaving its Field/Crop/Farm columns blank ("-"). Passing every org
    // crop season's id here instead makes this fetch cover the whole org,
    // matching what this Plan's crop zones can actually span.
    if (
      hasFetchedRef.current ||
      cropZones.length === 0 ||
      !cropSeasons?.length
    ) {
      return;
    }
    hasFetchedRef.current = true;
    reloadData({
      seasonIds: (cropSeasons as Array<{ id: string }>).map(season => season.id)
    });
  }, [cropZones.length, cropSeasons, reloadData]);

  const rows: PlanPropertiesRow[] = useMemo(() => {
    if (!propertyLandingPageData?.properties?.length) return [];

    const ids = cropZones.map(zone => zone.cropZoneIdValue);
    const resolved: Array<Record<string, any>> = getPlanProperties(
      propertyLandingPageData,
      ids,
      fieldsAndAreasGeoJSONCollection ?? {}
    );
    const resolvedById = new Map(resolved.map(zone => [zone.id, zone]));

    return cropZones.map(zone => {
      const match = resolvedById.get(zone.cropZoneIdValue);
      return {
        cropZoneId: zone.cropZoneIdValue,
        cropZoneName: match?.name,
        fieldId: match?.fieldId,
        fieldName: match?.fieldName,
        fieldGeometry: match?.features?.[0]?.geometry,
        farmId: match?.farmId,
        farmName: match?.farmName,
        cropId: match?.crop?.id,
        cropName: match?.crop?.name,
        cropZoneGeometry: match?.features?.[0]?.geometry,
        appliedArea: zone.areaValue,
        appliedAreaPercent:
          zone.coveragePct != null
            ? Maths.multiplyRound(zone.coveragePct, 100, 2)
            : undefined
      };
    });
  }, [cropZones, propertyLandingPageData, fieldsAndAreasGeoJSONCollection]);

  const {
    reactUiKitTablePaginationProp,
    reactUiKitTableOnChange,
    setTotalItemsCount,
    sortBy,
    sortDir,
    page
  } = useTablePaginationAndSort({
    pageSize: PAGE_SIZE,
    defaultSortBy: 'farmName',
    defaultSortDir: 'ascend'
  }) as any;

  const sortedRows = useMemo(() => tableSort(rows, sortBy, sortDir), [
    rows,
    sortBy,
    sortDir
  ]);

  useEffect(() => {
    setTotalItemsCount(sortedRows.length);
  }, [sortedRows.length, setTotalItemsCount]);

  const paginatedRows = useMemo(
    () => sortedRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [sortedRows, page]
  );

  const hasPaginationNavigation = sortedRows.length > PAGE_SIZE;

  const columns = useMemo(
    () => [
      {
        title: `Crop Zones (${sortedRows.length})`,
        dataIndex: 'cropZoneName',
        key: 'cropZoneName',
        fixed: true as const,
        width: 220,
        className: getHeaderSortClassName('cropZoneName', sortBy, sortDir),
        sorter: (a: PlanPropertiesRow, b: PlanPropertiesRow) =>
          (a.cropZoneName || '').localeCompare(b.cropZoneName || ''),
        sortOrder: sortBy === 'cropZoneName' && sortDir,
        render: (_: unknown, record: PlanPropertiesRow) => (
          <div className="flex items-center gap-2">
            <div className="shrink-0">
              <FieldIcon
                id={record.fieldId || 'unknown'}
                features={[
                  {
                    type: 'Feature',
                    geometry: cleanGeometry(record.cropZoneGeometry) || {
                      type: '',
                      coordinates: []
                    },
                    properties: {}
                  }
                ]}
                border="#868CA2"
                color="#DFE2E7"
                width={32}
                height={32}
              />
            </div>
            <div className="min-w-0">
              <div
                className="text-sm text-neutral-60 truncate"
                title={record.fieldName}
              >
                {record.fieldName || NOT_AVAILABLE}
              </div>
              <div
                className="text-sm text-neutral-1000 truncate"
                title={record.cropZoneName}
              >
                {record.cropZoneName || NOT_AVAILABLE}
              </div>
            </div>
          </div>
        )
      },
      {
        title: 'Crop',
        dataIndex: 'cropName',
        key: 'cropName',
        width: 180,
        className: getHeaderSortClassName('cropName', sortBy, sortDir),
        sorter: (a: PlanPropertiesRow, b: PlanPropertiesRow) =>
          (a.cropName || '').localeCompare(b.cropName || ''),
        sortOrder: sortBy === 'cropName' && sortDir,
        render: (_: unknown, record: PlanPropertiesRow) => (
          <div className="flex items-center gap-1 min-w-0">
            {record.cropName && (
              <div className="shrink-0">
                <CropIconMapping
                  cropObject={{ name: record.cropName }}
                  size={18}
                />
              </div>
            )}
            <div className="truncate" title={record.cropName}>
              {record.cropName || NOT_AVAILABLE}
            </div>
          </div>
        )
      },
      {
        title: 'Farm',
        dataIndex: 'farmName',
        key: 'farmName',
        width: 200,
        className: getHeaderSortClassName('farmName', sortBy, sortDir),
        sorter: (a: PlanPropertiesRow, b: PlanPropertiesRow) =>
          (a.farmName || '').localeCompare(b.farmName || ''),
        sortOrder: sortBy === 'farmName' && sortDir,
        render: (_: unknown, record: PlanPropertiesRow) => (
          <div className="truncate" title={record.farmName}>
            {record.farmName || NOT_AVAILABLE}
          </div>
        )
      },
      {
        title: <span className="block w-full text-right">Appl. Acreage</span>,
        dataIndex: 'appliedArea',
        key: 'appliedArea',
        width: 150,
        className: `justify-end text-right ${getHeaderSortClassName(
          'appliedArea',
          sortBy,
          sortDir
        )}`,
        sorter: (a: PlanPropertiesRow, b: PlanPropertiesRow) =>
          (a.appliedArea || 0) - (b.appliedArea || 0),
        sortOrder: sortBy === 'appliedArea' && sortDir,
        render: (_: unknown, record: PlanPropertiesRow) => (
          <div className="text-right">
            <div className="text-neutral-90 font-medium">
              {record.appliedArea != null
                ? `${record.appliedArea.toFixed(2)} ac`
                : NOT_AVAILABLE}
            </div>
            <div className="text-neutral-60 text-xs">
              {record.appliedAreaPercent != null
                ? `${record.appliedAreaPercent.toFixed(2)}%`
                : NOT_AVAILABLE}
            </div>
          </div>
        )
      }
    ],
    [sortBy, sortDir, sortedRows.length]
  );

  const tableScrollConfig = useTableScrollConfig({
    x: PROPERTIES_TABLE_SCROLL_X,
    rowCount: paginatedRows.length,
    desktopOffsetEm: PROPERTIES_TABLE_OFFSET_EM,
    leadingHeight: PROPERTIES_TABLE_LEADING_HEIGHT,
    wrapperSelector: OPERATION_DETAILS_TABS_WRAPPER_SELECTOR,
    containerBottomPaddingPx: PROPERTIES_TAB_BOTTOM_PADDING_PX
  });

  // Archived flags default to `false` since `getPlanProperties` carries no
  // archived state.
  const mapDrawerProperties: OperationPropertyVO[] = useMemo(
    () =>
      sortedRows.map((row: PlanPropertiesRow) => ({
        farmId: row.farmId ?? '',
        farmName: row.farmName ?? '',
        farmArchived: false,
        fieldId: row.fieldId ?? '',
        fieldName: row.fieldName ?? '',
        fieldGeometry: row.fieldGeometry,
        fieldArchived: false,
        cropZoneId: row.cropZoneId,
        cropZoneName: row.cropZoneName ?? '',
        cropZoneGeometry: row.cropZoneGeometry,
        cropZoneArea: row.appliedArea,
        appliedArea: row.appliedArea ?? 0,
        appliedAreaPercent: row.appliedAreaPercent ?? 0,
        cropZoneArchived: false,
        cropName: row.cropName ?? '',
        cropId: row.cropId ?? ''
      })),
    [sortedRows]
  );

  // FR-076: `reloadData()` (above) never fires for a Plan with zero crop
  // zones, so `usePopulateMapData`'s own `loading` flag never flips from its
  // initial `true` — gating on it alone left this tab spinning forever for
  // a Plan that genuinely has none. Distinguishes "still loading" from
  // "zero crop zones by design" via the same `cropZones.length` the fetch
  // effect itself gates on, so the empty state (`Table`'s `locale.emptyText`
  // below) becomes reachable.
  if (loading && cropZones.length > 0) {
    return (
      <div
        data-testid="plan-properties"
        className="flex items-center justify-center"
        style={{ height: 'calc(100vh - 33em)' }}
      >
        <LoadingSpinner message="" className="h-full" />
      </div>
    );
  }

  return (
    <div className="px-6 py-8" data-testid="plan-properties">
      {sortedRows.length > 0 && (
        <div className="flex justify-end mb-2">
          <Button
            type={ButtonType.primary}
            ghost
            size="large"
            onClick={() => setIsMapDrawerOpen(true)}
            style={{ width: '130px', height: '32px', padding: '4px 12px' }}
          >
            <div
              className="flex items-center gap-1"
              style={{ fontSize: '14px' }}
            >
              <MapIcon color="#14803C" size={16} />
              <span>View on Map</span>
            </div>
          </Button>
        </div>
      )}
      {sortedRows.length === 0 ? (
        <div className="bg-white w-full">
          <EmptyTableState
            title="No Properties Selected"
            overlayIcon={NoPropertiesSelectedIcon}
            containerClassName="w-full h-full pt-16px pb-32px flex flex-col justify-center items-center"
          />
        </div>
      ) : (
        <div
          className={`application-view-products-tab-table plan-properties-table-wrapper${
            hasPaginationNavigation
              ? ''
              : ' application-view-products-tab-table-no-pagination'
          }`}
        >
          <Table
            rowKey="cropZoneId"
            columns={columns}
            scroll={tableScrollConfig}
            pagination={reactUiKitTablePaginationProp}
            onChange={reactUiKitTableOnChange}
            dataSource={paginatedRows}
            data-testid="plan-properties-table"
          />
        </div>
      )}

      <ViewOnMapDrawer
        open={isMapDrawerOpen}
        onClose={() => setIsMapDrawerOpen(false)}
        properties={mapDrawerProperties}
        cropSeasonNames={cropSeasonNames}
      />
    </div>
  );
};

export default PlanPropertiesTab;
